import type { FilterField } from '@/shared/components/filter/types'
import type { FieldSchemaModel } from '@/shared/generated'
import type { TreeNodeType } from '@/shared/lib/tree'

import { useMemo } from 'react'
import { any, filter, map, paths, pipe, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import {
	isAnchorStageField,
	toAdaptedSchema,
	useColumnsSchemaQuery,
} from '@/entities/column'
import {
	toCategoriesTree,
	useBrandsQuery,
	useCategoriesQuery,
} from '@/entities/core'
import { useOptimizationGroupsQuery } from '@/entities/optimization-group'

import { getFiledConfig } from '@/shared/components/query-builder'
import { HiddenOnContext } from '@/shared/generated'
import { toOption } from '@/shared/tools/utils'

export const useFamilyRulesFieldConfig = () => {
	const { t } = useTranslation()

	/**
	 * Queries
	 */
	const brandsQuery = useBrandsQuery(
		{},
		{
			select: pipe([map(paths(['id', 'name'])), toOption]),
		}
	)
	const categoriesQuery = useCategoriesQuery<TreeNodeType[]>(
		{},
		{
			select: toCategoriesTree,
		}
	)
	const optimizationGroupsQuery = useOptimizationGroupsQuery({
		select: pipe([map(paths(['id', 'name'])), toOption]),
	})
	const fieldsQuery = useColumnsSchemaQuery<FilterField[]>(
		HiddenOnContext.AnchorClustering,
		{
			select: pipe([
				filter((item) => isAnchorStageField(item as FieldSchemaModel)),
				map((schema: FieldSchemaModel) => toAdaptedSchema(schema, t)),
			]),
		}
	)
	const fields = useMemo(() => {
		if (!fieldsQuery.data?.length) {
			return []
		} else {
			return fieldsQuery.data
		}
	}, [fieldsQuery.data])

	const fieldConfig = getFiledConfig(fields, {
		brands: brandsQuery.data || [],
		category_ids: categoriesQuery.data || [],
		optimization_group_id: optimizationGroupsQuery.data || [],
	})

	return {
		isLoading: any(prop('isLoading'), [
			brandsQuery,
			categoriesQuery,
			optimizationGroupsQuery,
			fieldsQuery,
		]),
		fieldConfig,
		fields,
	}
}
